/**
 * API contract shared by the Fastify server and the React front end. Every payload
 * shape crossing the network is described here, and the front end never redeclares a
 * response type: a field renamed on one side then fails to compile on the other,
 * instead of arriving as `undefined` in a browser weeks later.
 *
 * The package has no dependency, not even a type-only one. It is imported by a Node
 * process and by a browser bundle, and anything it pulled in would have to work in
 * both.
 */
/* ------------------------------------------------------------------ Languages */
/**
 * Languages the interface and the server's messages are written in.
 *
 * Declared here rather than in the front end because both ends need the same list:
 * the browser announces its choice with `Accept-Language`, and the server answers in
 * the language it read there. A locale absent from this list resolves back to
 * `DEFAULT_LOCALE` rather than being rejected — an unsupported language must degrade
 * to a readable page, never to an error.
 */
export const LOCALES = ['en', 'fr'];
/**
 * Language used when nothing else answers: an unrecognised browser preference, or a
 * request carrying no `Accept-Language` at all. English, because it is the language
 * the repository itself is written in.
 */
export const DEFAULT_LOCALE = 'en';
export function isLocale(value) {
    return typeof value === 'string' && LOCALES.includes(value);
}
/**
 * The supported language behind a language tag: `fr-CA` reads as `fr`.
 *
 * Region is dropped deliberately. The application translates a language, not a
 * variant, and matching `fr-FR` exactly would leave a Swiss browser in English.
 */
export function resolveLocale(tag) {
    if (!tag)
        return null;
    const base = tag.trim().toLowerCase().split('-')[0];
    return isLocale(base) ? base : null;
}
/* ------------------------------------------------------------------- Accounts */
/**
 * What a username may contain: letters, digits, and the three separators a person
 * types without thinking. No spaces, so a value can be trimmed without ambiguity;
 * no case sensitivity anywhere, since the column collates `NOCASE`.
 */
export const USERNAME_PATTERN = /^[a-zA-Z0-9._-]+$/;
export const USERNAME_MAX_LENGTH = 64;
/**
 * Minimum password length. Deliberately a length and not a character-class rule:
 * composition rules push people towards `P@ssw0rd!`, while length is what actually
 * costs an attacker. The accounts here are created from a shell by whoever operates
 * the instance, so there is no public sign-up to defend.
 */
export const PASSWORD_MIN_LENGTH = 10;
/* ------------------------------------------------------------------- Settings */
/**
 * Name shown in the tab, on the sign-in screen and on the installed icon. A default
 * rather than a required value: an instance must be able to start before anybody has
 * decided what to call it.
 */
export const DEFAULT_INSTANCE_NAME = 'kanto';
export const INSTANCE_NAME_MAX_LENGTH = 60;
export const DEFAULT_SETTINGS = {
    instanceName: DEFAULT_INSTANCE_NAME,
};
/* ----------------------------------------------------------------------- Tags */
/**
 * The shape a tag has to take. Lower case, no spaces, so `Work`, `work` and
 * `work ` cannot become three tags nobody can tell apart in a list — the server
 * normalises before it stores, and this is what it normalises towards.
 *
 * Not `TODO_TAG_…`, and not one constant per module: a tag is a single entity
 * belonging to the account, carried by every module through the same registry
 * (D260815). Two constants would be two rules the day somebody widened one.
 */
export const TAG_MAX_LENGTH = 32;
export const TAG_PATTERN = /^[a-z0-9][a-z0-9-]*$/;
/**
 * How many tags one row may carry.
 *
 * Eight is what still reads as a line in a list. Past that the tags are what the
 * row says and the title is what is left over, and the limit exists to keep the
 * chips beside a title rather than under it.
 */
export const TAG_MAX_PER_ENTITY = 8;
/* ---------------------------------------------------------------------- Todos */
/**
 * A title is a line, not a document. Five hundred characters is past the point
 * where anything still reads as a todo: what comes after belongs in a note, which
 * is a different module for exactly this reason.
 */
export const TODO_TITLE_MAX_LENGTH = 500;
/**
 * A due date is a **day**, never an instant: `2026-08-14`, with no time and no
 * timezone to arbitrate (D260814i). The pattern is the shape only — the server
 * also checks that the day exists, since this matches `2026-13-45` just as well.
 */
export const DUE_ON_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
/* ------------------------------------------------------------------ Reminders */
/** Same limit as a todo title, and for the same reason. */
export const REMINDER_TITLE_MAX_LENGTH = 500;
/**
 * An **instant**, and the contrast with `DUE_ON_PATTERN` is the whole point: a
 * todo carries a day and arbitrates no timezone (D260814i), while a reminder is a
 * moment the server has to be able to compare against its own clock (D260814m).
 *
 * The offset is not optional. `2026-08-14T14:30:00` names no instant until
 * somebody decides whose afternoon it is, and the one thing a reminder cannot
 * afford is for that somebody to be whichever process happened to parse it. What
 * is stored and returned is always the `Z` form, normalised on write — text
 * ordering only holds for the canonical spelling.
 */
export const REMIND_AT_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d{1,3})?)?(Z|[+-]\d{2}:\d{2})$/;
/* ------------------------------------------------------------------- Folders */
/**
 * A folder name is a label in a tree, not a sentence. Sixty characters is past
 * the width of the column that draws it, and a name that has to be truncated to
 * be shown is a name that identifies nothing.
 */
export const FOLDER_NAME_MAX_LENGTH = 60;
/**
 * How deep the tree may go, counting the root as depth 1.
 *
 * Not a technical limit — the queries are recursive-free and a hundred levels
 * would work. It is a limit on what stays navigable in a column 240 pixels wide,
 * and on what somebody can still hold in their head. Eight is already more than
 * a personal instance has ever needed.
 */
export const FOLDER_MAX_DEPTH = 8;
/* ---------------------------------------------------------------------- Notes */
/**
 * A note's own title, when it has one. Same limit as a todo's, and for the same
 * reason: a title is a line.
 */
export const NOTE_TITLE_MAX_LENGTH = 200;
/**
 * How long a note may be. A hundred thousand characters is some forty pages —
 * past anything anybody types into a single note, and comfortably under the
 * `bodyLimit` Fastify enforces on the request that carries it, which is what
 * this number exists to stay below.
 */
export const NOTE_BODY_MAX_LENGTH = 100_000;
/** Roughly one line of a list. Long enough to recognise a note, short enough to scan. */
export const NOTE_EXCERPT_LENGTH = 160;
/**
 * The title of a note that has not been given one: its first non-empty line.
 *
 * Here rather than on the server because the **editor needs it while somebody is
 * typing** — the heading at the top of the screen follows the first line of the
 * body, and there is no round trip in between. Two implementations would diverge
 * the day one of them decided what to do about `# Heading`, which is exactly the
 * case below.
 *
 * The heading marker is stripped: a note starting `# Meeting` is called
 * "Meeting", not "# Meeting". Nothing else is — stripping every kind of markup
 * would be a second markdown parser, in a package that may not have one.
 */
export function deriveNoteTitle(body) {
    for (const line of body.split('\n')) {
        const text = line.replace(/^\s*#{1,6}\s+/, '').trim();
        if (text !== '')
            return text.slice(0, NOTE_TITLE_MAX_LENGTH);
    }
    return '';
}
/* --------------------------------------------------------------------- Search */
/** How many hits one request may ask for, and what it gets without asking. */
export const SEARCH_LIMIT_DEFAULT = 20;
export const SEARCH_LIMIT_MAX = 50;
/**
 * The two control characters that mark a matched run inside an excerpt.
 *
 * The server hands back `…STX`match`ETX`…` rather than `<mark>`, and the front
 * end splits on them to build the element itself. **Returning markup would make
 * this the one string in the whole application a browser has to interpret**, and
 * an excerpt is cut out of a note somebody else could have written.
 *
 * Control characters rather than a printable sentinel because no keyboard
 * produces them: any pair of printable characters is a pair somebody eventually
 * types into a note.
 */
export const SEARCH_MARK_START = '';
export const SEARCH_MARK_END = '';
//# sourceMappingURL=index.js.map